import { Command } from '@repo/ui/command'
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'

import { useBeneficiaryListAsSheet } from '@/components/beneficiary/list/sheet/hook'
import { SearchInput, SearchInputRef } from '@/components/command-center/SearchInput'
import { useBreakpoint } from '@/hooks'
import { defaultFilterTemplate } from '@/store/beneficiary-list-filters/slice'

const CommandCenterContext = createContext<{
  query: string
  setQuery: (query: string) => void
} | null>(null)

// Custom hook to manage open/close state and keyboard shortcuts
function useCommandCenterOpen({ isShortViewport }: { isShortViewport: boolean }) {
  const [open, setOpen] = useState(false)
  const handleOpen = useCallback(() => setOpen(true), [])
  const handleClose = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (isShortViewport) return
    const k = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        handleOpen()
      }
    }
    // Remove any existing listener before adding a new one
    document.removeEventListener('keydown', k)
    document.addEventListener('keydown', k)
    return () => document.removeEventListener('keydown', k)
  }, [isShortViewport, handleOpen])

  useEffect(() => {
    if (isShortViewport || !open) return
    const down = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        handleClose()
      }
    }
    // Remove any existing listener before adding a new one
    document.removeEventListener('keydown', down)
    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [open, handleClose, isShortViewport])

  return {
    handleClose,
    handleOpen,
    setIsOpen: setOpen,
    open,
  }
}

// Context provider as a component
export function CommandCenterProvider({ children }: { children: React.ReactNode }) {
  const [query, setQuery] = useState<string>('')
  return (
    <CommandCenterContext.Provider
      value={{
        query,
        setQuery,
      }}
    >
      {children}
    </CommandCenterContext.Provider>
  )
}

export function CommandCenter({ onCreateBeneficiary }: { onCreateBeneficiary: () => void }) {
  const ctx = useContext(CommandCenterContext)
  if (!ctx) throw new Error('CommandCenterContext is not provided')
  const { isShortViewport } = useBreakpoint()
  const inputRef = useRef<SearchInputRef>(null)

  const { open, handleClose } = useCommandCenterOpen({
    isShortViewport,
  })

  const handleCreateBeneficiary = useCallback(() => {
    handleClose()
    onCreateBeneficiary()
  }, [onCreateBeneficiary, handleClose])

  useEffect(() => {
    if (isShortViewport || !open) return
    const event = (e: KeyboardEvent) => {
      if (e.key === 'b' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        handleCreateBeneficiary()
      }
    }
    // Remove any existing listener before adding a new one
    document.removeEventListener('keydown', event)
    document.addEventListener('keydown', event)
    return () => document.removeEventListener('keydown', event)
  }, [open, handleCreateBeneficiary, isShortViewport])

  const { setOpen } = useBeneficiaryListAsSheet()
  const openBeneficiaryList = () => setOpen(true, { filterTemplate: defaultFilterTemplate })

  useEffect(() => {
    if (!open) return
    openBeneficiaryList()
  }, [open])

  return (
    <Command
      shouldFilter={false}
      className="border-primary-200 border-px relative w-fit overflow-visible sm:w-full"
    >
      <SearchInput
        ref={inputRef}
        open={false}
        query={ctx.query}
        setQuery={ctx.setQuery}
        onClick={openBeneficiaryList}
      />
    </Command>
  )
}
